import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import { SessionProvider, useSession } from './auth/session';
import { Spinner } from './components/ui';
import { ApiError } from './api/client';
import { Login } from './screens/Login';
import { Reset } from './screens/Reset';
import { Projects } from './screens/Projects';
import { Documents } from './screens/Documents';
import { DocumentPage } from './screens/Document';
import { DraftViewer } from './screens/DraftViewer';
import { PrintPage } from './screens/PrintPage';
import { Audit } from './screens/Audit';
import { Feedback } from './screens/Feedback';
import { Users } from './screens/Users';
import { Pair } from './screens/Pair';
import { Notifications } from './screens/Notifications';
import { Guide } from './screens/Guide';
import { Share } from './screens/Share';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
});

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useSession();
  const loc = useLocation();
  if (loading) return <div className="app"><Spinner label="Checking your session" /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset" element={<Reset />} />
            <Route path="/s/:token" element={<Share />} />
            <Route path="/" element={<Navigate to="/projects" replace />} />
            <Route path="/projects" element={<RequireAuth><Projects /></RequireAuth>} />
            <Route path="/projects/:projectId" element={<RequireAuth><Documents /></RequireAuth>} />
            <Route path="/documents/:documentId" element={<RequireAuth><DocumentPage /></RequireAuth>} />
            <Route path="/documents/:documentId/drafts/:draftId" element={<RequireAuth><DraftViewer /></RequireAuth>} />
            <Route path="/documents/:documentId/audit" element={<RequireAuth><Audit /></RequireAuth>} />
            <Route path="/documents/:documentId/feedback" element={<RequireAuth><Feedback /></RequireAuth>} />
            <Route path="/users" element={<RequireAuth><Users /></RequireAuth>} />
            <Route path="/pair" element={<RequireAuth><Pair /></RequireAuth>} />
            <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
            <Route path="/guide" element={<RequireAuth><Guide /></RequireAuth>} />
            <Route path="/print/:documentId/:draftId" element={<RequireAuth><PrintPage /></RequireAuth>} />
            <Route path="*" element={<Navigate to="/projects" replace />} />
          </Routes>
        </BrowserRouter>
      </SessionProvider>
    </QueryClientProvider>
  );
}
